import React, { useState } from "react";
import {
    Card, CardBody, Col, Container, Form, FormGroup,
    Input, Label, Button, Row, Spinner,
} from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";
import AnimatedBackground from "../../components/Animatedbackground";

const api = new APIClient();

type Step = "email" | "otp" | "reset";

const ResidentForgotPassword: React.FC = () => {
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>("email");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // ── Step 1: Send OTP ──────────────────────────────
    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return toast.warning("Email required!");
        if (!/\S+@\S+\.\S+/.test(email)) return toast.warning("Enter valid email!");

        setLoading(true);
        try {
            await api.create("/api/resident/forgot-password/send-otp", { email: email.trim().toLowerCase() });
            toast.success("OTP sent to your email!");
            setStep("otp");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Failed to send OTP!");
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Verify OTP ────────────────────────────
    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (otp.length !== 6) return toast.warning("Enter 6-digit OTP!");

        setLoading(true);
        try {
            await api.create("/api/resident/forgot-password/verify-otp", {
                email: email.trim().toLowerCase(),
                otp: otp.trim(),
            });
            toast.success("OTP verified!");
            setStep("reset");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Invalid or expired OTP!");
        } finally {
            setLoading(false);
        }
    };

    // ── Step 3: Reset Password ────────────────────────
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword.length < 6) return toast.warning("Password min 6 characters!");
        if (newPassword !== confirmPassword) return toast.warning("Passwords don't match!");

        setLoading(true);
        try {
            await api.create("/api/resident/forgot-password/reset", {
                email: email.trim().toLowerCase(),
                otp: otp.trim(),
                newPassword,
            });
            toast.success("Password reset successfully! Please login.");
            setTimeout(() => navigate("/resident/login"), 2000);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Reset failed!");
        } finally {
            setLoading(false);
        }
    };

    // ── Step indicator ────────────────────────────────
    const steps = [
        { key: "email", label: "Email", icon: "ri-mail-line" },
        { key: "otp",   label: "OTP",   icon: "ri-shield-keyhole-line" },
        { key: "reset", label: "Reset", icon: "ri-lock-password-line" },
    ];

    const stepIndex = steps.findIndex(s => s.key === step);

    return (
          <AnimatedBackground>
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={5}>
                        <Card className="border-0 shadow-lg" style={{ borderRadius: "20px" }}>
                            <CardBody className="p-4 p-md-5">

                                {/* Header */}
                                <div className="text-center mb-4">
                                    <div style={{
                                        width: "70px", height: "70px", borderRadius: "50%",
                                        background: "linear-gradient(135deg, #764ba2, #764ba2)",
                                        display: "inline-flex", alignItems: "center",
                                        justifyContent: "center", marginBottom: "16px",
                                    }}>
                                        <i className="ri-lock-unlock-line" style={{ fontSize: "32px", color: "#fff" }} />
                                    </div>
                                    <h3 className="fw-bold mb-1">Forgot Password</h3>
                                    <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                                        {step === "email" && "Enter your registered email"}
                                        {step === "otp"   && "Enter the OTP sent to your email"}
                                        {step === "reset" && "Set your new password"}
                                    </p>
                                </div>

                                {/* Step Indicator */}
                                <div className="d-flex align-items-center justify-content-center mb-4 gap-0">
                                    {steps.map((s, i) => (
                                        <React.Fragment key={s.key}>
                                            <div className="d-flex flex-column align-items-center" style={{ minWidth: 64 }}>
                                                <div style={{
                                                    width: 38, height: 38, borderRadius: "50%",
                                                    background: i <= stepIndex
                                                        ? "linear-gradient(135deg, #764ba2, #764ba2)"
                                                        : "#e9ecef",
                                                    display: "flex", alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "background 0.3s",
                                                }}>
                                                    <i className={s.icon} style={{
                                                        fontSize: 16,
                                                        color: i <= stepIndex ? "#fff" : "#adb5bd",
                                                    }} />
                                                </div>
                                                <span style={{
                                                    fontSize: 11, marginTop: 4,
                                                    color: i <= stepIndex ? "#764ba2" : "#adb5bd",
                                                    fontWeight: i === stepIndex ? 700 : 400,
                                                }}>
                                                    {s.label}
                                                </span>
                                            </div>
                                            {i < steps.length - 1 && (
                                                <div style={{
                                                    flex: 1, height: 2, marginBottom: 20,
                                                    background: i < stepIndex
                                                        ? "linear-gradient(90deg, #764ba2, #764ba2)"
                                                        : "#e9ecef",
                                                    transition: "background 0.3s",
                                                }} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>

                                {/* ── STEP 1: Email ── */}
                                {step === "email" && (
                                    <Form onSubmit={handleSendOtp}>
                                        <FormGroup className="mb-4">
                                            <Label className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
                                                <i className="ri-mail-line me-1 text-primary" />Registered Email *
                                            </Label>
                                            <Input
                                                type="email"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                style={{
                                                    height: "44px", borderRadius: "10px",
                                                    border: "1.5px solid #e2e8f0", fontSize: "14px",
                                                }}
                                            />
                                        </FormGroup>

                                        <Button type="submit" color="primary" block disabled={loading}
                                            style={{
                                                height: "48px", fontSize: "15px", fontWeight: 600,
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #764ba2, #764ba2)",
                                                border: "none",
                                            }}>
                                            {loading
                                                ? <><Spinner size="sm" className="me-2" />Sending OTP...</>
                                                : <><i className="ri-send-plane-line me-2" />Send OTP</>
                                            }
                                        </Button>
                                    </Form>
                                )}

                                {/* ── STEP 2: OTP ── */}
                                {step === "otp" && (
                                    <Form onSubmit={handleVerifyOtp}>
                                        <div style={{
                                            background: "#f0f4ff", border: "1px solid #c7d2fe",
                                            borderRadius: "10px", padding: "12px 16px",
                                            marginBottom: "20px", fontSize: "13px",
                                        }}>
                                            <i className="ri-mail-check-line text-primary me-1" />
                                            OTP sent to <strong>{email}</strong>
                                            <button type="button"
                                                onClick={() => setStep("email")}
                                                style={{
                                                    background: "none", border: "none",
                                                    color: "#764ba2", fontSize: 12,
                                                    marginLeft: 8, cursor: "pointer",
                                                    textDecoration: "underline",
                                                }}>
                                                Change
                                            </button>
                                        </div>

                                        <FormGroup className="mb-3">
                                            <Label className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
                                                <i className="ri-shield-keyhole-line me-1 text-primary" />Enter 6-digit OTP *
                                            </Label>
                                            <Input
                                                type="text"
                                                value={otp}
                                                onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                                placeholder="• • • • • •"
                                                maxLength={6}
                                                style={{
                                                    height: "52px", borderRadius: "10px",
                                                    border: "1.5px solid #e2e8f0",
                                                    fontSize: "22px", fontWeight: 700,
                                                    letterSpacing: "10px", textAlign: "center",
                                                }}
                                            />
                                        </FormGroup>

                                        <Button type="submit" color="primary" block
                                            disabled={loading || otp.length !== 6}
                                            style={{
                                                height: "48px", fontSize: "15px", fontWeight: 600,
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #764ba2, #764ba2)",
                                                border: "none", marginBottom: "12px",
                                            }}>
                                            {loading
                                                ? <><Spinner size="sm" className="me-2" />Verifying...</>
                                                : <><i className="ri-shield-check-line me-2" />Verify OTP</>
                                            }
                                        </Button>

                                        {/* Resend OTP */}
                                        <ResendOtp email={email} />
                                    </Form>
                                )}

                                {/* ── STEP 3: New Password ── */}
                                {step === "reset" && (
                                    <Form onSubmit={handleResetPassword}>
                                        <FormGroup className="mb-3">
                                            <Label className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
                                                <i className="ri-lock-line me-1 text-primary" />New Password *
                                            </Label>
                                            <div style={{ position: "relative" }}>
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    value={newPassword}
                                                    onChange={e => setNewPassword(e.target.value)}
                                                    placeholder="Min 6 characters"
                                                    style={{
                                                        height: "44px", borderRadius: "10px",
                                                        border: "1.5px solid #e2e8f0",
                                                        fontSize: "14px", paddingRight: "40px",
                                                    }}
                                                />
                                                <button type="button"
                                                    onClick={() => setShowPassword(p => !p)}
                                                    style={{
                                                        position: "absolute", right: "12px", top: "50%",
                                                        transform: "translateY(-50%)", background: "none",
                                                        border: "none", color: "#6b7280",
                                                    }}>
                                                    <i className={`ri-${showPassword ? "eye-off" : "eye"}-line`} />
                                                </button>
                                            </div>
                                        </FormGroup>

                                        <FormGroup className="mb-4">
                                            <Label className="fw-semibold mb-1" style={{ fontSize: "13px" }}>
                                                <i className="ri-lock-2-line me-1 text-primary" />Confirm Password *
                                            </Label>
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                value={confirmPassword}
                                                onChange={e => setConfirmPassword(e.target.value)}
                                                placeholder="Re-enter password"
                                                style={{
                                                    height: "44px", borderRadius: "10px",
                                                    border: `1.5px solid ${confirmPassword && newPassword !== confirmPassword ? "#dc3545" : "#e2e8f0"}`,
                                                    fontSize: "14px",
                                                }}
                                            />
                                            {confirmPassword && newPassword !== confirmPassword && (
                                                <small className="text-danger">Passwords don't match</small>
                                            )}
                                        </FormGroup>

                                        <Button type="submit" color="primary" block
                                            disabled={loading}
                                            style={{
                                                height: "48px", fontSize: "15px", fontWeight: 600,
                                                borderRadius: "10px",
                                                background: "linear-gradient(135deg, #764ba2, #764ba2)",
                                                border: "none",
                                            }}>
                                            {loading
                                                ? <><Spinner size="sm" className="me-2" />Resetting...</>
                                                : <><i className="ri-shield-check-line me-2" />Reset Password</>
                                            }
                                        </Button>
                                    </Form>
                                )}

                                {/* Back to login */}
                                <div className="text-center mt-4">
                                    <Link to="/resident/login"
                                        style={{ color: "#764ba2", fontWeight: 600,
                                            textDecoration: "none", fontSize: "14px" }}>
                                        <i className="ri-arrow-left-line me-1" />Back to Login
                                    </Link>
                                </div>

                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
       </AnimatedBackground>
    );
};

// ── Resend OTP sub-component ──────────────────────────
const ResendOtp: React.FC<{ email: string }> = ({ email }) => {
    const [countdown, setCountdown] = useState(30);
    const [resending, setResending] = useState(false);

    React.useEffect(() => {
        if (countdown <= 0) return;
        const t = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown]);

    const handleResend = async () => {
        setResending(true);
        try {
            await new APIClient().create("/api/resident/forgot-password/send-otp",
                { email: email.trim().toLowerCase() });
            toast.success("OTP resent!");
            setCountdown(30);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Resend failed!");
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="text-center" style={{ fontSize: "13px", color: "#6b7280" }}>
            {countdown > 0 ? (
                <>Resend OTP in <strong style={{ color: "#764ba2" }}>{countdown}s</strong></>
            ) : (
                <button type="button" onClick={handleResend} disabled={resending}
                    style={{
                        background: "none", border: "none", color: "#764ba2",
                        fontWeight: 600, cursor: "pointer", textDecoration: "underline",
                    }}>
                    {resending ? "Resending..." : "Resend OTP"}
                </button>
            )}
        </div>
    );
};

export default ResidentForgotPassword;